"use client"
import React, { useState } from "react";
import PropTypes from "prop-types";
import { Modal } from "./modal";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

export function LoginModal({ isOpen, onClose, onSignupClick, setAuthData }) {
  const rawBackendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const backend_url = rawBackendUrl.startsWith("http://") || rawBackendUrl.startsWith("https://")
    ? rawBackendUrl
    : `https://${rawBackendUrl}`;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await fetch(`${backend_url}/api/token/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Réponse du backend:", data);

        // Récupérer le rôle correctement
        const userRole = data.user?.role || data.role;

        const authData = {
          accessToken: data.access,
          refreshToken: data.refresh,
          role: userRole,
          permissions: data.user?.permissions || data.permissions || [],
          userId: data.user?.id || data.user_id,
          email: data.user?.email || data.email,
          name: data.user?.name || data.name,
        };

        localStorage.setItem("access_token", data.access);
        localStorage.setItem("refresh_token", data.refresh);
        localStorage.setItem("auth_data", JSON.stringify(authData));
        localStorage.setItem("userRole", userRole);
        console.log("Auth data saved:", authData);
        console.log("Rôle de l'utilisateur:", userRole);

        onClose();

        // Redirection selon le rôle
        if (userRole === "Admin") {
          console.log("🔐 Redirection vers /admin-dashboard");
          navigate("/admin-dashboard");
        } else if (userRole === "Manager") {
          console.log("🔐 Redirection vers /dashboard");
          navigate("/dashboard");
        } else {
          console.log("🔐 Redirection vers /consulter-reunion");
          navigate("/consulter-reunion");
        }

        // Déclencher un événement pour mettre à jour le Layout
        window.dispatchEvent(new CustomEvent('auth-updated'));

      } else {
        const errorText = await response.text();
        let errorData = {};
        try {
          errorData = errorText ? JSON.parse(errorText) : {};
        } catch {
          errorData = {};
        }
        setError(errorData.detail || "Identifiants invalides");
      }
    } catch (err) {
      setError("Une erreur est survenue. Réessayez.");
      console.error("Login error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
        <div className="p-6">
          <h2 className="text-3xl font-semibold text-center text-[#5e17eb] mb-6">Se connecter</h2>

          <form onSubmit={handleSubmit} className="space-y-2 w-full">
            {error && <p className="text-red-500 text-center text-sm">{error}</p>}

            <div className="space-y-2 w-full">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="email.."
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="border-gray-200 focus:border-[#5e17eb] focus:ring-[#5e17eb]"
              />
            </div>

            <div className="space-y-2 w-full">
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                placeholder="mot de passe .."
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="border-gray-200 focus:border-[#5e17eb] focus:ring-[#5e17eb]"
              />
            </div>

            <div className="text-sm text-right">
              <button
                type="button"
                className="text-gray-600 hover:text-[#5e17eb] transition-colors"
                onClick={() => setShowForgotPassword(true)}
              >
                Mot de passe perdu ?
              </button>
            </div>

            <Button
              type="submit"
              className="bg-gradient-to-r from-[#5e17eb] to-[#7c3aed] text-white w-full hover:from-[#7c3aed] hover:to-[#5e17eb]"
              disabled={loading}
            >
              {loading ? "Connexion..." : "Continuer"}
            </Button>
          </form>

          <div className="text-center mt-4 text-sm text-gray-600">
            Pas encore inscrit ?{" "}
            <button
              onClick={onSignupClick}
              className="text-[#5e17eb] hover:text-[#7c3aed] rounded-md transition-colors"
            >
              Créer un compte
            </button>
          </div>
        </div>
      </Modal>

      {/* Forgot Password Modal - Same message as before but with nice design */}
      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
      />
    </>
  );
}

// Forgot Password Modal Component - Same functionality as the alert, just beautiful design
function ForgotPasswordModal({ isOpen, onClose }) {
  const [email, setEmail] = useState("");

  const handleSendResetLink = () => {
    // Same functionality as the original alert
    // Just shows a message about contacting admin
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="p-6">
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-[#f3edff] rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-[#5e17eb]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </div>
        </div>

        <h2 className="text-2xl font-semibold text-center text-[#5e17eb] mb-3">
          Mot de passe oublié ?
        </h2>
        
        <p className="text-center text-gray-600 mb-6">
          Veuillez contacter l'administrateur pour réinitialiser votre mot de passe.
        </p>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-blue-700 text-sm text-center">
            📧 Contactez l'administrateur à : <strong>admin@polymeet.com</strong>
          </p>
        </div>

        <Button
          onClick={handleSendResetLink}
          className="w-full bg-gradient-to-r from-[#5e17eb] to-[#7c3aed] text-white hover:from-[#7c3aed] hover:to-[#5e17eb]"
        >
          J'ai compris
        </Button>
        
        <div className="text-center mt-4">
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-[#5e17eb] text-sm transition-colors"
          >
            ← Retour à la connexion
          </button>
        </div>
      </div>
    </Modal>
  );
}

ForgotPasswordModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

LoginModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSignupClick: PropTypes.func.isRequired,
  setAuthData: PropTypes.func.isRequired,
};

export default LoginModal;